import { ChangeDetectionStrategy, Component, HostListener, computed, effect, inject, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { CeRequestStatus } from '../../models/ce-request.model';
import { CeRequestStateService } from '../../services/ce-request-state.service';
import { B2bUserDto, ProjectEngineerDto, ProjectsApiService, ProjectDto } from '../../services/projects-api.service';
import { CurrentUserService } from '../../services/current-user.service';

interface UserDirectoryItem {
  id: string;
  name: string;
  role: string;
  department: string;
  backToBackId: string;
}

interface IncomingB2bRequest {
  id: number;
  name: string;
  role: string;
  department: string;
  backToBackId: string;
  isRequester: boolean;
}

type RequestsSearchFieldKey = 'estimateNumber' | 'projectTitle';

interface RequestsSearchFieldOption {
  key: RequestsSearchFieldKey;
  label: string;
  paramName: string;
}

interface RequestsSearchToken {
  key: RequestsSearchFieldKey;
  label: string;
  value: string;
}

const REQUESTS_SEARCH_FIELD_OPTIONS: readonly RequestsSearchFieldOption[] = [
  { key: 'estimateNumber', label: 'Estimate Number', paramName: 'EstimateNumber.FilterValues' },
  { key: 'projectTitle', label: 'Project Title', paramName: 'ProjectTitle.FilterValues' },
] as const;

@Component({
  selector: 'app-my-ce-requests',
  imports: [DatePipe, FormsModule, NgbPaginationModule, MatIconModule, MatAutocompleteModule],
  templateUrl: './my-ce-requests.html',
  styleUrl: './my-ce-requests.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyCeRequests {
  private readonly router = inject(Router);
  private readonly state = inject(CeRequestStateService);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly currentUserService = inject(CurrentUserService);

  protected readonly user = this.currentUserService.profile;
  protected readonly projects = signal<ProjectDto[]>([]);
  protected readonly projectsLoading = signal(false);
  protected readonly totalRecords = signal(0);
  protected readonly statusFilterOpen = signal(false);
  protected readonly dateFilterOpen = signal(false);
  protected readonly selectedStatusFilters = signal<CeRequestStatus[]>([]);
  protected readonly appliedStatusFilters = signal<CeRequestStatus[]>([]);
  protected readonly selectedStartDate = signal('');
  protected readonly selectedEndDate = signal('');
  protected readonly appliedStartDate = signal('');
  protected readonly appliedEndDate = signal('');
  protected readonly searchFieldOptions = REQUESTS_SEARCH_FIELD_OPTIONS;
  protected readonly searchFieldDropdownOpen = signal(false);
  protected readonly searchTokens = signal<RequestsSearchToken[]>([]);
  protected readonly appliedSearchTokens = signal<RequestsSearchToken[]>([]);
  protected readonly availableSearchFieldOptions = computed(() => {
    const selectedKeys = new Set(this.searchTokens().map(token => token.key));
    return this.searchFieldOptions.filter(option => !selectedKeys.has(option.key));
  });

  protected readonly userDirectory = signal<UserDirectoryItem[]>([]);

  protected readonly selectedUserId = signal('');
  protected readonly showAllB2BOptions = signal(false);
  protected readonly b2bUser = signal<UserDirectoryItem | null>(null);
  protected readonly b2bRequestPending = signal(false);
  protected readonly b2bRequestSending = signal(false);
  protected readonly b2bRequestError = signal('');
  protected readonly incomingB2bRequest = signal<IncomingB2bRequest | null>(null);
  protected readonly incomingB2bState = signal<'pending' | 'active' | 'none'>('none');
  protected readonly incomingActionSending = signal(false);
  protected readonly incomingActionError = signal('');
  protected readonly pendingAction = signal<'confirm' | 'reject' | null>(null);
  protected readonly showRemoveConfirm = signal(false);
  protected readonly showCancelB2bConfirm = signal(false);

  protected readonly availableUsers = computed(() => {
    const currentUser = this.user();
    const currentObjectId = currentUser.objectId?.trim().toLowerCase();
    const currentName = currentUser.name?.trim().toLowerCase();

    return this.userDirectory().filter(userItem => {
      const userObjectId = userItem.id.trim().toLowerCase();
      const userName = userItem.name.trim().toLowerCase();

      if (currentObjectId && userObjectId === currentObjectId) {
        return false;
      }

      if (currentName && userName === currentName) {
        return false;
      }

      return true;
    });
  });

  constructor() {
    void this.currentUserService.load();
    void this.loadUserDirectory();
    effect(() => {
      const page = this.currentPage();
      void untracked(() => this.loadProjects(page));
    });
  }

  private async loadUserDirectory(): Promise<void> {
    const [engineersResult, b2bResult] = await Promise.allSettled([
      firstValueFrom(this.projectsApi.getProjectEngineers()),
      firstValueFrom(this.projectsApi.getB2bUsers()),
    ]);

    if (engineersResult.status === 'fulfilled') {
      this.userDirectory.set(this.mapProjectEngineers(engineersResult.value));
    } else {
      this.userDirectory.set([]);
    }

    if (b2bResult.status === 'fulfilled') {
      this.syncIncomingB2bRequest(b2bResult.value);
    } else {
      this.incomingB2bRequest.set(null);
      this.incomingB2bState.set('none');
    }
  }

  private mapProjectEngineers(engineers: readonly ProjectEngineerDto[]): UserDirectoryItem[] {
    const unique = new Map<string, UserDirectoryItem>();

    for (const engineer of engineers) {
      const id = engineer.objectId?.trim();
      const name = engineer.fullName?.trim();
      if (!id || !name) {
        continue;
      }

      unique.set(id, {
        id,
        name,
        role: 'Project Engineer',
        department: 'Project Engineering',
        backToBackId: id,
      });
    }

    return Array.from(unique.values());
  }

  private syncIncomingB2bRequest(users: readonly B2bUserDto[]): void {
    const hasIdentity = (user: B2bUserDto): boolean => {
      const backToBackId = user.backToBackId?.trim() ?? '';
      const partnerName = user.partnerName?.trim() ?? '';
      return !!backToBackId && !!partnerName;
    };

    const pending = users.find((user) => user.statusType === 0 && hasIdentity(user));
    const active = users.find((user) => user.statusType === 1 && hasIdentity(user));
    const candidate = pending ?? active;

    if (!candidate) {
      this.incomingB2bRequest.set(null);
      this.incomingB2bState.set('none');
      return;
    }

    const profile = this.user();
    this.incomingB2bRequest.set({
      id: candidate.id,
      name: candidate.partnerName!.trim(),
      role: profile.positionName || 'Cost Estimator',
      department: profile.department || 'Cost Engineering',
      backToBackId: candidate.backToBackId!.trim(),
      isRequester: candidate.isRequester,
    });
    this.incomingB2bState.set(candidate.statusType === 1 ? 'active' : 'pending');
    this.incomingActionSending.set(false);
    this.incomingActionError.set('');
    this.b2bUser.set(null);
    this.b2bRequestPending.set(false);
    this.b2bRequestSending.set(false);
    this.b2bRequestError.set('');
    this.showRemoveConfirm.set(false);
  }

  private async loadProjects(page: number): Promise<void> {
    this.projectsLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.projectsApi.getProjects(page, this.pageSize, this.buildProjectsFilterQuery()),
      );
      this.projects.set(response.items);
      this.totalRecords.set(response.totalRecords);
    } catch (error) {
      console.error(error);
      this.projects.set([]);
      this.totalRecords.set(0);
    } finally {
      this.projectsLoading.set(false);
    }
  }

  protected projectStatusLabel(statusType: number): CeRequestStatus {
    switch (statusType) {
      case 0: return 'Draft';
      case 1: return 'Submitted';
      case 2: return 'Assigned CE';
      case 3: return 'Under Development';
      case 4: return 'Issued';
      case 5: return 'Pending Additional Information';
      case 6: return 'Canceled';
      default: return 'Draft';
    }
  }

  protected formatEstimateNumber(project: ProjectDto): string {
    return project.estimateNumber?.trim() ?? '';
  }

  protected readonly pageSize = 10;
  protected readonly currentPage = signal(1);
  protected readonly search = signal('');
  protected readonly statusFilterOptions: readonly CeRequestStatus[] = [
    'Draft',
    'Submitted',
    'Assigned CE',
    'Under Development',
    'Pending Additional Information',
    'Issued',
    'Canceled',
  ];

  protected readonly filteredRequests = computed(() => {
    return this.projects();
  });

  protected readonly filteredTotal = computed(() => {
    return this.totalRecords();
  });

  protected onSearchInputChange(value: string): void {
    this.search.set(value);
    this.searchFieldDropdownOpen.set(!!value.trim());
  }

  protected onSearchInputFocus(): void {
    this.searchFieldDropdownOpen.set(!!this.search().trim());
  }

  protected addSearchToken(fieldKey: RequestsSearchFieldKey, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const value = this.search().trim();
    if (!value) {
      this.searchFieldDropdownOpen.set(false);
      return;
    }

    const option = this.searchFieldOptions.find(item => item.key === fieldKey);
    if (!option) {
      return;
    }

    const hasSameField = this.searchTokens().some(token => token.key === fieldKey);
    if (hasSameField) {
      return;
    }

    this.searchTokens.update(current => [
      ...current,
      { key: option.key, label: option.label, value },
    ]);
    this.search.set('');
    this.searchFieldDropdownOpen.set(false);
  }

  protected removeSearchToken(index: number): void {
    this.searchTokens.update(current => current.filter((_, idx) => idx !== index));
  }

  protected onSearchClick(): void {
    this.searchFieldDropdownOpen.set(false);
    this.appliedSearchTokens.set([...this.searchTokens()]);
    this.reloadFromFirstPage();
  }

  protected toggleStatusFilter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dateFilterOpen.set(false);
    this.statusFilterOpen.update(v => !v);
  }

  protected toggleDateFilter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.statusFilterOpen.set(false);
    this.dateFilterOpen.update(v => !v);
  }

  protected isStatusFilterSelected(status: CeRequestStatus): boolean {
    return this.selectedStatusFilters().includes(status);
  }

  protected hasAppliedStatusFilters(): boolean {
    return this.appliedStatusFilters().length > 0;
  }

  protected hasAppliedDateFilters(): boolean {
    return !!this.appliedStartDate().trim() || !!this.appliedEndDate().trim();
  }

  protected toggleStatusFilterValue(status: CeRequestStatus): void {
    this.selectedStatusFilters.update(current =>
      current.includes(status)
        ? current.filter(item => item !== status)
        : [...current, status],
    );
  }

  protected clearStatusFilters(): void {
    this.selectedStatusFilters.set([]);
    this.appliedStatusFilters.set([]);
    this.reloadFromFirstPage();
    this.statusFilterOpen.set(false);
  }

  protected applyStatusFilters(): void {
    this.appliedStatusFilters.set([...this.selectedStatusFilters()]);
    this.reloadFromFirstPage();
    this.statusFilterOpen.set(false);
  }

  protected clearDateFilters(): void {
    this.selectedStartDate.set('');
    this.selectedEndDate.set('');
    this.appliedStartDate.set('');
    this.appliedEndDate.set('');
    this.reloadFromFirstPage();
    this.dateFilterOpen.set(false);
  }

  protected applyDateFilters(): void {
    this.appliedStartDate.set(this.selectedStartDate().trim());
    this.appliedEndDate.set(this.selectedEndDate().trim());
    this.reloadFromFirstPage();
    this.dateFilterOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.statusFilterOpen() && !this.searchFieldDropdownOpen() && !this.dateFilterOpen()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      this.statusFilterOpen.set(false);
      this.searchFieldDropdownOpen.set(false);
      this.dateFilterOpen.set(false);
      return;
    }

    if (
      target.closest('.status-filter-dropdown') ||
      target.closest('.status-filter-trigger') ||
      target.closest('.date-filter-dropdown') ||
      target.closest('.date-filter-trigger') ||
      target.closest('.requests-search-field-dropdown') ||
      target.closest('.search-input')
    ) {
      return;
    }

    this.statusFilterOpen.set(false);
    this.searchFieldDropdownOpen.set(false);
    this.dateFilterOpen.set(false);
  }

  private reloadFromFirstPage(): void {
    if (this.currentPage() !== 1) {
      this.currentPage.set(1);
      return;
    }

    void this.loadProjects(1);
  }

  private buildProjectsFilterQuery(): Record<string, readonly (string | number)[] | undefined> {
    const query: Record<string, readonly (string | number)[] | undefined> = {};

    for (const token of this.appliedSearchTokens()) {
      const option = this.searchFieldOptions.find(item => item.key === token.key);
      if (!option) {
        continue;
      }

      query[option.paramName] = [token.value];
    }

    const statusValues = this.appliedStatusFilters()
      .map(status => this.mapStatusLabelToType(status))
      .filter((value): value is number => value !== null);

    if (statusValues.length) {
      query['ProjectStatusType.FilterValues'] = statusValues;
    }

    const startDate = this.appliedStartDate().trim();
    if (startDate) {
      query['SubmittedDate.StartDate'] = [startDate];
    }

    const endDate = this.appliedEndDate().trim();
    if (endDate) {
      query['SubmittedDate.EndDate'] = [endDate];
    }

    return query;
  }

  private mapStatusLabelToType(status: CeRequestStatus): number | null {
    switch (status) {
      case 'Draft': return 0;
      case 'Submitted': return 1;
      case 'Assigned CE': return 2;
      case 'Under Development': return 3;
      case 'Issued': return 4;
      case 'Pending Additional Information': return 5;
      case 'Canceled': return 6;
      default: return null;
    }
  }

  protected statusClass(status: CeRequestStatus): string {
    return 'status status--' + status.toLowerCase().replace(/\s+/g, '-');
  }

  protected initials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected addUserById(userId: string): void {
    this.selectedUserId.set(userId);
    this.showAllB2BOptions.set(false);

    const normalizedInput = userId.trim().toLowerCase();
    if (!normalizedInput) {
      return;
    }

    const found = this.userDirectory().find((user) => {
      const byId = user.id.toLowerCase() === normalizedInput;
      const byName = user.name.toLowerCase() === normalizedInput;
      const byDisplay = `${user.name} · ${user.department}`.toLowerCase() === normalizedInput;
      return byId || byName || byDisplay;
    });

    if (!found) {
      return;
    }

    this.b2bUser.set(found);
    this.b2bRequestPending.set(false);
    this.b2bRequestError.set('');
    this.showRemoveConfirm.set(false);

    this.selectedUserId.set('');
  }

  protected openB2BOptions(trigger: MatAutocompleteTrigger): void {
    this.showAllB2BOptions.set(true);
    trigger.openPanel();
  }

  protected onB2BBlur(): void {
    setTimeout(() => {
      this.showAllB2BOptions.set(false);
    }, 120);
  }

  protected readonly filteredAvailableUsers = computed(() => {
    const users = this.availableUsers();
    if (this.showAllB2BOptions()) {
      return users;
    }

    const query = this.selectedUserId().trim().toLowerCase();
    if (!query) {
      return users;
    }

    return users.filter(user => {
      const display = `${user.name} · ${user.department}`.toLowerCase();
      return (
        user.name.toLowerCase().includes(query) ||
        user.id.toLowerCase().includes(query) ||
        display.includes(query)
      );
    });
  });

  protected clearB2BUser(): void {
    this.b2bUser.set(null);
    this.b2bRequestPending.set(false);
    this.b2bRequestSending.set(false);
    this.b2bRequestError.set('');
    this.showRemoveConfirm.set(false);
    this.selectedUserId.set('');
  }

  protected askConfirmIncomingRequest(): void {
    if (this.incomingB2bState() !== 'pending') {
      return;
    }

    this.pendingAction.set('confirm');
  }

  protected askRejectIncomingRequest(): void {
    if (this.incomingB2bState() !== 'pending') {
      return;
    }

    this.pendingAction.set('reject');
  }

  protected async confirmPendingAction(): Promise<void> {
    const action = this.pendingAction();

    if (action === 'confirm') {
      const incoming = this.incomingB2bRequest();
      if (!incoming) {
        this.pendingAction.set(null);
        return;
      }

      this.incomingActionError.set('');
      this.incomingActionSending.set(true);
      try {
        await firstValueFrom(this.projectsApi.acceptB2bUser(incoming.id));
        this.incomingB2bState.set('active');
        this.pendingAction.set(null);
      } catch (error) {
        console.error(error);
        this.incomingActionError.set('Unable to confirm B2B request. Please try again.');
      } finally {
        this.incomingActionSending.set(false);
      }
      return;
    }

    if (action === 'reject') {
      const incoming = this.incomingB2bRequest();
      if (incoming) {
        this.incomingActionSending.set(true);
        try {
          await firstValueFrom(this.projectsApi.deleteB2bUser(incoming.id));
        } catch (error) {
          console.error(error);
          // ignore errors, clear state regardless
        } finally {
          this.incomingActionSending.set(false);
        }
      }
      this.incomingB2bState.set('none');
      this.incomingB2bRequest.set(null);
      this.incomingActionError.set('');
      this.pendingAction.set(null);
      return;
    }

    this.pendingAction.set(null);
  }

  protected cancelPendingAction(): void {
    this.pendingAction.set(null);
  }

  protected async sendB2BRequest(): Promise<void> {
    const user = this.b2bUser();
    if (!user || this.b2bRequestSending()) {
      return;
    }

    const backToBackId = user.backToBackId.trim();
    if (!backToBackId) {
      this.b2bRequestError.set('Selected user does not have BackToBack ID.');
      return;
    }

    this.b2bRequestError.set('');
    this.b2bRequestSending.set(true);
    try {
      await firstValueFrom(this.projectsApi.addB2bUser({ backToBackId }));
      window.location.reload();
    } catch (error) {
      console.error(error);
      this.b2bRequestPending.set(false);
      this.b2bRequestError.set('Unable to send B2B request. Please try again.');
    } finally {
      this.b2bRequestSending.set(false);
    }
  }

  protected askRemoveB2BUser(): void {
    this.showRemoveConfirm.set(true);
  }

  protected askCancelOwnB2bRequest(): void {
    this.showCancelB2bConfirm.set(true);
  }

  protected cancelCancelOwnB2bRequest(): void {
    this.showCancelB2bConfirm.set(false);
  }

  protected async cancelOwnB2bRequest(): Promise<void> {
    this.showCancelB2bConfirm.set(false);
    const incoming = this.incomingB2bRequest();
    if (!incoming) {
      return;
    }
    try {
      await firstValueFrom(this.projectsApi.deleteB2bUser(incoming.id));
    } catch (error) {
      console.error(error);
      // ignore errors, clear state regardless
    }
    this.incomingB2bRequest.set(null);
    this.incomingB2bState.set('none');
  }

  protected confirmRemoveB2BUser(): void {
    this.b2bUser.set(null);
    this.b2bRequestPending.set(false);
    this.b2bRequestSending.set(false);
    this.b2bRequestError.set('');
    this.selectedUserId.set('');
    this.showRemoveConfirm.set(false);
  }

  protected cancelRemoveB2BUser(): void {
    this.showRemoveConfirm.set(false);
  }

  protected createRequest(): void {
    this.state.resetDraft();
    this.router.navigate(['/project-team']);
  }

  protected viewProject(project: ProjectDto): void {
    this.router.navigate(['/view-request'], { queryParams: { projectId: project.id } });
  }
}

